import { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { useLab } from '../i18n/LabContext';

const EMOJIS = ['🎁','🥤','☕','🧃','🍔','🍕','🍩','🎧','⏱️','🖱️','🎮','💎','🏆','⚡'];

const EMPTY = { name: '', emoji: '🎁', type: 'bar', value: '', cost_coins: '', product_id: '' };

export default function Rewards() {
  const { labId, currentLab, refreshLabs } = useLab();
  const [tab, setTab] = useState('catalog');
  const [rewards, setRewards] = useState([]);
  const [claims, setClaims] = useState([]);
  const [snackProducts, setSnackProducts] = useState([]);
  const [snackDisabled, setSnackDisabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [coinRateInput, setCoinRateInput] = useState('');
  const [coinRateSaving, setCoinRateSaving] = useState(false);

  const loadRewards = useCallback(() => {
    if (!labId) return;
    setLoading(true);
    api.get(`/rewards/admin?lab_id=${labId}`)
      .then(r => setRewards(r.data || []))
      .catch(() => setRewards([]))
      .finally(() => setLoading(false));
  }, [labId]);

  const loadSnackProducts = useCallback(() => {
    if (!labId) return;
    api.get(`/snack/public-products?lab_id=${labId}`)
      .then(r => { setSnackProducts(r.data || []); setSnackDisabled(false); })
      .catch(e => {
        setSnackProducts([]);
        setSnackDisabled(e.response?.status === 403);
      });
  }, [labId]);

  const loadClaims = useCallback(() => {
    if (!labId) return;
    api.get(`/rewards/claims?lab_id=${labId}&status=pending`)
      .then(r => setClaims(r.data || []))
      .catch(() => setClaims([]));
  }, [labId]);

  useEffect(() => { loadRewards(); loadSnackProducts(); }, [loadRewards, loadSnackProducts]);

  useEffect(() => {
    setCoinRateInput(currentLab?.coin_earn_rate != null ? String(currentLab.coin_earn_rate) : '0');
  }, [currentLab]);

  const saveCoinRate = async () => {
    if (!labId) return;
    const rate = parseFloat(coinRateInput);
    if (isNaN(rate) || rate < 0 || rate > 100) return alert('Foiz 0 dan 100 gacha bo\'lishi kerak');
    setCoinRateSaving(true);
    try {
      await api.patch(`/owner/labs/${labId}/settings`, { coin_earn_rate: rate });
      refreshLabs();
    } catch (e) {
      alert(e.response?.data?.error || 'Xato');
    } finally {
      setCoinRateSaving(false);
    }
  };
  useEffect(() => {
    if (tab !== 'claims') return;
    loadClaims();
    const t = setInterval(loadClaims, 10000);
    return () => clearInterval(t);
  }, [tab, loadClaims]);

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    if (form.type === 'bar' && !form.product_id) return setErr('Bar mahsulotini tanlang');
    if (form.type === 'bar' && !form.name.trim()) return setErr('Nom kiritish shart');
    if (form.type !== 'bar' && !form.name.trim()) return setErr('Nom kiritish shart');
    if (!form.cost_coins || parseInt(form.cost_coins) <= 0) return setErr('Tanga narxi kerak');
    if (form.type === 'time' && (!form.value || parseInt(form.value) <= 0)) return setErr('Vaqt miqdori kerak (daqiqa)');
    setSaving(true);
    try {
      const body = {
        lab_id: labId,
        name: form.name.trim(),
        emoji: form.emoji,
        type: form.type,
        value: form.type === 'time' ? parseInt(form.value) : 0,
        cost_coins: parseInt(form.cost_coins),
        product_id: form.type === 'bar' && form.product_id ? parseInt(form.product_id) : null,
      };
      if (editId) {
        await api.put(`/rewards/${editId}`, body);
      } else {
        await api.post('/rewards', body);
      }
      setForm(EMPTY);
      setEditId(null);
      loadRewards();
    } catch (e) {
      setErr(e.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (r) => {
    setEditId(r.id);
    setForm({
      name: r.name, emoji: r.emoji, type: r.type,
      value: String(r.value || ''), cost_coins: String(r.cost_coins),
      product_id: r.product_id ? String(r.product_id) : '',
    });
    setErr('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const toggleActive = async (r) => {
    try {
      await api.put(`/rewards/${r.id}`, { is_active: !r.is_active });
      loadRewards();
    } catch {}
  };

  const del = async (r) => {
    if (!window.confirm(`"${r.name}" o'chirilsinmi?`)) return;
    await api.delete(`/rewards/${r.id}`).catch(() => {});
    loadRewards();
  };

  const markDone = async (id) => {
    await api.put(`/rewards/claims/${id}/done`).catch(() => {});
    loadClaims();
  };

  const pendingBar = claims.filter(c => c.reward_type === 'bar');

  const st = {
    wrap: { padding: '24px', fontFamily: 'Inter, sans-serif', maxWidth: 900 },
    h1: { fontSize: 22, fontWeight: 700, marginBottom: 20, color: 'var(--text)' },
    tabs: { display: 'flex', gap: 8, marginBottom: 24 },
    tab: (active) => ({
      padding: '8px 18px', borderRadius: 8, border: `1px solid ${active ? 'var(--cyan)' : 'var(--border)'}`, cursor: 'pointer',
      fontWeight: 600, fontSize: 13,
      background: active ? 'rgba(0,212,255,0.1)' : 'transparent', color: active ? 'var(--cyan)' : 'var(--text2)',
    }),
    badge: { background: '#ef4444', color: '#fff', borderRadius: 99, padding: '1px 7px', fontSize: 11, marginLeft: 6 },
    card: { background: 'var(--card2)', border: '1px solid var(--border)', borderRadius: 12, padding: 20, marginBottom: 20 },
    label: { display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text2)', marginBottom: 6 },
    input: { width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 14, boxSizing: 'border-box', background: 'var(--bg2)', color: 'var(--text)' },
    row: { display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 14 },
    btn: (color) => ({ padding: '10px 20px', borderRadius: 8, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 13, background: color || '#7c3aed', color: '#fff' }),
    err: { color: '#ef4444', fontSize: 13, marginTop: 8 },
    table: { width: '100%', borderCollapse: 'collapse' },
    th: { padding: '10px 12px', textAlign: 'left', fontSize: 12, color: 'var(--text3)', fontWeight: 600, borderBottom: '1px solid var(--border)' },
    td: { padding: '12px', borderBottom: '1px solid var(--border)', fontSize: 14, color: 'var(--text)' },
    chip: (color) => ({ display: 'inline-block', padding: '3px 10px', borderRadius: 99, fontSize: 12, fontWeight: 600, background: color + '22', color }),
  };

  return (
    <div style={st.wrap}>
      <div style={st.h1}>🎁 Sovg'alar tizimi</div>

      {/* Tanga stavkasi sozlamasi */}
      <div style={{ background: 'var(--card2)', border: '1px solid var(--purple)', borderRadius: 14, padding: 20, marginBottom: 24 }}>
        <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 4, color: 'var(--purple)' }}>🪙 Tanga stavkasi (Coin Earn Rate)</div>
        <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 16 }}>
          Mijoz balans to'ldirganida qancha foiz tanga berilsin. Masalan: 5% → 10,000 so'm = 500 🪙
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <input
            type="number"
            min="0"
            max="100"
            step="0.5"
            value={coinRateInput}
            onChange={e => setCoinRateInput(e.target.value)}
            placeholder="0"
            style={{ width: 100, padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 14, background: 'var(--bg2)', color: 'var(--text)' }}
          />
          <span style={{ fontSize: 13, color: 'var(--text2)' }}>%</span>
          <button onClick={saveCoinRate} disabled={coinRateSaving} style={st.btn(coinRateSaving ? '#9ca3af' : '#7c3aed')}>
            {coinRateSaving ? 'Saqlanmoqda...' : 'Saqlash'}
          </button>
          {parseFloat(currentLab?.coin_earn_rate) > 0 && (
            <span style={{ fontSize: 12, color: 'var(--cyan)', fontWeight: 600 }}>
              Hozir: {currentLab.coin_earn_rate}% • 10,000 so'm = {Math.round(10000 * currentLab.coin_earn_rate / 100)} 🪙
            </span>
          )}
          {(!currentLab?.coin_earn_rate || parseFloat(currentLab.coin_earn_rate) === 0) && (
            <span style={{ fontSize: 12, color: '#9ca3af' }}>O'chirilgan (0%)</span>
          )}
        </div>
      </div>

      <div style={st.tabs}>
        <button style={st.tab(tab === 'catalog')} onClick={() => setTab('catalog')}>Katalog</button>
        <button style={st.tab(tab === 'claims')} onClick={() => setTab('claims')}>
          Buyurtmalar {pendingBar.length > 0 && <span style={st.badge}>{pendingBar.length}</span>}
        </button>
      </div>

      {tab === 'catalog' && (
        <>
          {/* FORM */}
          <div style={st.card}>
            <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 16 }}>
              {editId ? '✏️ Tahrirlash' : '➕ Yangi sovg\'a qo\'shish'}
            </div>

            <form onSubmit={submit}>
              <div style={st.row}>
                <div style={{ width: 140 }}>
                  <label style={st.label}>Turi</label>
                  <select style={st.input} value={form.type}
                    onChange={e => setForm(f => ({ ...f, type: e.target.value, product_id: '', name: '' }))}>
                    <option value="bar">🍔 Bar mahsulot</option>
                    <option value="time">⏱️ Bonus vaqt</option>
                  </select>
                </div>

                {form.type === 'bar' ? (
                  <div style={{ flex: 2, minWidth: 200 }}>
                    <label style={st.label}>Bar mahsuloti</label>
                    <select
                      style={{ ...st.input, color: form.product_id ? undefined : '#9ca3af' }}
                      value={form.product_id}
                      onChange={e => {
                        const pid = e.target.value;
                        const prod = snackProducts.find(p => String(p.id) === pid);
                        setForm(f => ({
                          ...f,
                          product_id: pid,
                          name: prod ? prod.name : f.name,
                          emoji: prod?.emoji || f.emoji,
                        }));
                      }}
                    >
                      <option value="">— Mahsulot tanlang —</option>
                      {snackProducts.map(p => (
                        <option key={p.id} value={p.id}>
                          {p.emoji || '🍽'} {p.name} — {Number(p.price).toLocaleString()} so'm
                        </option>
                      ))}
                    </select>
                    {snackProducts.length === 0 && (
                      <div style={{ fontSize: 11, color: '#f59e0b', marginTop: 4 }}>
                        {snackDisabled
                          ? '⚠️ Snack-bar bu klub uchun yoqilmagan. SnackPOS → Sozlamalar bo\'limida yoqing.'
                          : '⚠️ Bar mahsulotlari topilmadi. Avval SnackPOS sahifasida mahsulot qo\'shing.'}
                      </div>
                    )}
                  </div>
                ) : (
                  <div style={{ flex: 2, minWidth: 200 }}>
                    <label style={st.label}>Sovg'a nomi</label>
                    <input style={st.input} placeholder="30 daqiqa bonus" value={form.name}
                      onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
                  </div>
                )}

                {form.type === 'time' && (
                  <div style={{ width: 130 }}>
                    <label style={st.label}>Daqiqa</label>
                    <input style={st.input} type="number" min="5" placeholder="30" value={form.value}
                      onChange={e => setForm(f => ({ ...f, value: e.target.value }))} />
                  </div>
                )}
                <div style={{ width: 140 }}>
                  <label style={st.label}>🪙 Narxi (tanga)</label>
                  <input style={st.input} type="number" min="1" placeholder="300" value={form.cost_coins}
                    onChange={e => setForm(f => ({ ...f, cost_coins: e.target.value }))} />
                </div>
              </div>

              <div style={{ marginBottom: 14 }}>
                <label style={st.label}>Emoji</label>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {EMOJIS.map(em => (
                    <button key={em} type="button"
                      style={{ fontSize: 22, background: form.emoji === em ? 'rgba(124,58,237,0.2)' : 'var(--bg2)',
                        border: form.emoji === em ? '2px solid #7c3aed' : '1px solid var(--border)',
                        borderRadius: 8, width: 44, height: 44, cursor: 'pointer',
                        filter: 'drop-shadow(0 0 1px rgba(0,0,0,0.3))' }}
                      onClick={() => setForm(f => ({ ...f, emoji: em }))}>
                      {em}
                    </button>
                  ))}
                </div>
              </div>

              {err && <div style={st.err}>{err}</div>}

              <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
                <button type="submit" style={st.btn()} disabled={saving}>
                  {saving ? 'Saqlanmoqda...' : editId ? 'Saqlash' : "Qo'shish"}
                </button>
                {editId && (
                  <button type="button" style={st.btn('#64748b')}
                    onClick={() => { setEditId(null); setForm(EMPTY); setErr(''); }}>
                    Bekor qilish
                  </button>
                )}
              </div>
            </form>
          </div>

          {/* LIST */}
          <div style={st.card}>
            <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 16 }}>Mavjud sovg'alar</div>
            {loading ? <div style={{ color: '#94a3b8' }}>Yuklanmoqda...</div> : rewards.length === 0 ? (
              <div style={{ color: '#94a3b8', textAlign: 'center', padding: 24 }}>Hali sovg'a qo'shilmagan</div>
            ) : (
              <table style={st.table}>
                <thead>
                  <tr>
                    <th style={st.th}>Sovg'a</th>
                    <th style={st.th}>Turi</th>
                    <th style={st.th}>Miqdori</th>
                    <th style={st.th}>Narxi</th>
                    <th style={st.th}>Holat</th>
                    <th style={st.th}></th>
                  </tr>
                </thead>
                <tbody>
                  {rewards.map(r => (
                    <tr key={r.id}>
                      <td style={st.td}>
                        <span style={{ fontSize: 18, marginRight: 8 }}>{r.emoji}</span>
                        {r.name}
                        {r.type === 'bar' && !r.product_id && (
                          <button
                            onClick={() => startEdit(r)}
                            style={{ fontSize: 11, color: '#fff', background: '#f59e0b', border: 'none',
                              borderRadius: 6, padding: '2px 8px', marginLeft: 8, cursor: 'pointer', fontWeight: 600 }}>
                            ⚠️ Mahsulot ulash
                          </button>
                        )}
                      </td>
                      <td style={st.td}>
                        <span style={st.chip(r.type === 'time' ? '#0891b2' : '#d97706')}>
                          {r.type === 'time' ? '⏱️ Vaqt' : '🍔 Bar'}
                        </span>
                      </td>
                      <td style={st.td}>
                        {r.type === 'time' ? `${r.value} daqiqa` : (r.product_name || '—')}
                      </td>
                      <td style={st.td}><strong>🪙 {r.cost_coins}</strong></td>
                      <td style={st.td}>
                        <button onClick={() => toggleActive(r)}
                          style={{ ...st.chip(r.is_active ? '#16a34a' : '#dc2626'), border: 'none', cursor: 'pointer' }}>
                          {r.is_active ? '✅ Faol' : '⛔ Nofaol'}
                        </button>
                      </td>
                      <td style={{ ...st.td, display: 'flex', gap: 8 }}>
                        <button onClick={() => startEdit(r)} style={st.btn('#3b82f6')}>Tahrirlash</button>
                        <button onClick={() => del(r)} style={st.btn('#ef4444')}>O'chirish</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {tab === 'claims' && (
        <div style={st.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div style={{ fontSize: 15, fontWeight: 700 }}>Kutilayotgan buyurtmalar</div>
            <button onClick={loadClaims} style={st.btn('#64748b')}>🔄 Yangilash</button>
          </div>

          {claims.length === 0 ? (
            <div style={{ color: '#94a3b8', textAlign: 'center', padding: 32 }}>Hozircha buyurtma yo'q ✅</div>
          ) : (
            <table style={st.table}>
              <thead>
                <tr>
                  <th style={st.th}>Gamer</th>
                  <th style={st.th}>Sovg'a</th>
                  <th style={st.th}>PC</th>
                  <th style={st.th}>Vaqt</th>
                  <th style={st.th}>Holat</th>
                  <th style={st.th}></th>
                </tr>
              </thead>
              <tbody>
                {claims.map(c => (
                  <tr key={c.id}>
                    <td style={st.td}><strong>{c.user_name || '—'}</strong></td>
                    <td style={st.td}>
                      <span style={{ fontSize: 16, marginRight: 6 }}>{rewards.find(r => r.id === c.reward_id)?.emoji || '🎁'}</span>
                      {c.reward_name}
                      {c.reward_type === 'time' && (
                        <span style={{ ...st.chip('#0891b2'), marginLeft: 8 }}>Avtomatik qo'shildi ✅</span>
                      )}
                    </td>
                    <td style={st.td}>{c.computer_number || '—'}</td>
                    <td style={{ ...st.td, color: '#64748b', fontSize: 12 }}>
                      {new Date(c.created_at).toLocaleTimeString('uz-UZ', { hour: '2-digit', minute: '2-digit' })}
                    </td>
                    <td style={st.td}>
                      <span style={st.chip(c.reward_type === 'time' ? '#16a34a' : '#d97706')}>
                        {c.reward_type === 'time' ? '⏱️ Vaqt (avto)' : '🍔 Yetkazish kerak'}
                      </span>
                    </td>
                    <td style={st.td}>
                      {c.reward_type === 'bar' && (
                        <button onClick={() => markDone(c.id)} style={st.btn('#16a34a')}>✅ Yetkazildi</button>
                      )}
                      {c.reward_type === 'time' && (
                        <button onClick={() => markDone(c.id)} style={st.btn('#94a3b8')}>Yopish</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
